package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.screenshots.doc.DesignIssueCode.OUTSIDE_CANVAS
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.TEXT_CROSSES_SEAM
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.TEXT_OUTSIDE_SAFE_AREA
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.geom.aabb
import dev.lingvo.shared.screenshots.geom.times
import dev.lingvo.shared.screenshots.geom.toParent
import dev.lingvo.shared.screenshots.render.Affine
import kotlin.math.floor

/**
 * The geometric warnings of SPEC-B 1.4: text across a panorama seam, a layer wholly off the strip, text outside the
 * safe area. Computed on world bounds (through every enclosing group), and only for finite geometry: a box the
 * validator already refuses gets no warning on top.
 *
 * LOW_CONTRAST needs the OKLab and WCAG maths of SPEC-B 7.2 (task B7) and is not emitted yet.
 */
internal object DesignWarnings {
    private const val TOLERANCE = 0.5

    fun page(sink: IssueSink, board: Board, page: Page, path: String) {
        val width = board.canvas.width.toDouble()
        val height = board.canvas.height.toDouble()
        val slots = page.slotIds.size
        if (width <= 0 || height <= 0 || slots == 0) return
        val strip = Rect(0.0, 0.0, width * slots, height)
        val safe = board.safeArea ?: Insets(top = 0.04 * height, right = 0.05 * width, bottom = 0.04 * height, left = 0.05 * width)
        if (!finite(safe)) return
        walk(page.layers, "$path/layers", Affine.IDENTITY, depth = 0) { layer, layerPath, bounds ->
            if (layer is BackgroundLayer || layer.base.hidden) return@walk
            if (!bounds.intersects(strip)) {
                sink.add(OUTSIDE_CANVAS, "$layerPath/base/box", "This layer is entirely outside the canvas.")
            }
            if (layer !is TextLayer) return@walk
            val crossesSeam = (1 until slots).any { k -> bounds.x < k * width - TOLERANCE && bounds.right > k * width + TOLERANCE }
            if (crossesSeam) {
                sink.add(TEXT_CROSSES_SEAM, "$layerPath/base/box", "Text crosses a seam between two screenshots; the store shows them apart.")
                return@walk
            }
            val slot = floor((bounds.x + bounds.w / 2) / width).toInt().coerceIn(0, slots - 1)
            val safeRect = Rect(
                slot * width + safe.left,
                safe.top,
                width - safe.left - safe.right,
                height - safe.top - safe.bottom,
            )
            if (!safeRect.contains(bounds, TOLERANCE)) {
                sink.add(TEXT_OUTSIDE_SAFE_AREA, "$layerPath/base/box", "Text reaches outside the safe area, where the store may crop it.")
            }
        }
    }

    private fun walk(
        layers: List<Layer>,
        path: String,
        parent: Affine,
        depth: Int,
        visit: (Layer, String, Rect) -> Unit,
    ) {
        layers.forEachIndexed { index, layer ->
            val box = layer.base.box
            if (!finite(box) || box.w <= 0 || box.h <= 0) return@forEachIndexed
            val layerPath = "$path/$index"
            visit(layer, layerPath, aabb(box, parent))
            if (layer is GroupLayer && depth < DesignLimits.MAX_GROUP_DEPTH) {
                walk(layer.children, "$layerPath/children", parent * box.toParent(), depth + 1, visit)
            }
        }
    }

    private fun finite(box: Box): Boolean =
        box.x.isFinite() && box.y.isFinite() && box.w.isFinite() && box.h.isFinite() && box.rotation.isFinite()

    private fun finite(insets: Insets): Boolean =
        insets.top.isFinite() && insets.right.isFinite() && insets.bottom.isFinite() && insets.left.isFinite()
}
